import { useMemo } from 'react';
import cn from 'classnames';
import moment from 'moment';

import S from 'constants/string.const';
import { useGilt, useTranslation } from 'providers/GILT.provider';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import { isPatchTestValid } from 'utils/predicates/isPatchTestValid.util';

import NotesContainer from 'clientProfile/components/BasicProfile/Notes';
import ShowTags from 'clientProfile/components/BasicProfile/ShowTags';

import CN from './BWClientHeader.module.scss';

export const BWClientHeader = ({ client, editClient, tag, clientTags, goToEditClientProfile, goToPatchView, goToClientProfile, noShow }) => {
  const { t } = useTranslation();
  const { formatDate } = useGilt();

  const response = editClient?.response;

  const phone = client?.phone || response?.phone;
  const birthday = client?.birthday || response?.birthday;

  const patchDate = useMemo(() => {
    if (client?.patch_test_date) {
      return client.patch_test_date;
    }
    if (response?.patch_test_date) {
      return response.patch_test_date;
    }

    return null;
  }, [client?.patch_test_date, response?.patch_test_date]);

  const birthdayLabel = useMemo(() => {
    if (!birthday) return null;
    return (
      <small>
        {t('scheduler.booking.bwClientHeader.birthday')} {birthday[0]} {moment.monthsShort(birthday[1] - 1)}
      </small>
    );
  }, [birthday, t]);

  const pronouns = client?.pronouns || response?.pronouns;
  const formattedPronouns = Array.isArray(pronouns) ? pronouns.slice(0, 4).join('/') : null;
  return (
    <div
      data-bem="BWClientHeader"
      data-tag={fromAnyToTrimmed(tag)}
      className={cn({
        'bw-client-header': true,
        [CN.component]: true,
      })}
    >
      <div>
        <h2 className="f-b">
          {client.user_name}
          <span className={CN.profilePronouns}>{formattedPronouns && ` (${formattedPronouns})`}</span>
        </h2>
        <div className="client-prompt-buttons 2">
          <div className="prompt-item">
            {!phone && (
              <div className="prompt" tabIndex="0" role="button" onClick={goToEditClientProfile || alwaysNoop}>
                {t('scheduler.booking.bwClientHeader.noMobile')}
              </div>
            )}
            {S.space}
          </div>

          {!client.phone && <div className="prompt-item dot">{S.dot}</div>}

          <div className="prompt-item">
            {S.space}
            {birthdayLabel}

            {!birthday && (
              <div className="prompt" tabIndex="0" role="button" onClick={goToEditClientProfile || alwaysNoop}>
                {t('scheduler.booking.bwClientHeader.noBirthday')}
              </div>
            )}
          </div>
          <div className="prompt-item dot">{S.dot}</div>
          <div className="prompt-item">
            {patchDate ? (
              isPatchTestValid(patchDate) ? (
                <>
                  {t('scheduler.booking.bwClientHeader.patchTest')}
                  {S.space}
                  {formatDate(patchDate, { hideTime: true, hideWeekday: true })}
                </>
              ) : (
                <div className="prompt" tabIndex="0" role="button" onClick={goToPatchView || alwaysNoop}>
                  {t('scheduler.booking.bwClientHeader.patchTestExpired')}
                </div>
              )
            ) : (
              <div className="prompt" tabIndex="0" role="button" onClick={goToPatchView || alwaysNoop}>
                {t('scheduler.booking.bwClientHeader.noPatchTest')}
              </div>
            )}
          </div>
        </div>
      </div>

      {fromAnyToBool(clientTags) && <ShowTags tagList={clientTags} />}
      {fromAnyToBool(client.notes) && <NotesContainer notes={client.notes} />}

      <div role="button" tabIndex="0" className="btn btn-violet-link" onClick={goToClientProfile} style={{ display: 'inline-block' }}>
        {t('scheduler.booking.bwClientHeader.viewProfileButton')}
      </div>
      {noShow}
    </div>
  );
};
